

// components/common/Pagination.tsx
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  hasNextPage?: boolean;
  isLoading?: boolean;
  className?: string;
}

const PRIMARY = '#A8523A';
const PRIMARY_DARK = '#8F4430';
const TEXT = '#2D2D2D';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  hasNextPage = false,
  isLoading = false,
  className = '',
}: PaginationProps) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Show max 7 page buttons

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const handlePageChange = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages && !isLoading) {
      onPageChange(page);
    }
  };

  return (
    <div className={`flex flex-col sm:flex-row p-4 items-center justify-between gap-4   ${className}`}>
      {/* Page info */}
      <div style={{ color: 'rgba(45,45,45,0.7)' }} className="text-sm order-2 sm:order-1">
        <span style={{ color: TEXT }} className="font-medium">Page {currentPage}</span>
        {' of '}
        <span style={{ color: TEXT }} className="font-medium">
          {hasNextPage ? `${totalPages}+` : totalPages}
        </span>
      </div>

      {/* Pagination controls */}
      <nav className="flex items-center gap-1 order-1 sm:order-2 " aria-label="Pagination">
        {/* First page */}
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || isLoading}
          className={`group relative p-2 rounded-lg transition-all duration-200`}
          title="First page"
          aria-label="Go to first page"
          style={{
            color: currentPage === 1 || isLoading ? 'rgba(45,45,45,0.25)' : 'rgba(45,45,45,0.7)',
            border: 'none',
            background: 'transparent',
          }}
        >
          <ChevronsLeft className="w-5 h-5" />
        </button>

        {/* Previous page */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className={`group relative px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5`}
          aria-label="Go to previous page"
          style={{
            color: currentPage === 1 || isLoading ? 'rgba(45,45,45,0.25)' : 'rgba(45,45,45,0.8)',
          }}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline" style={{ color: 'rgba(45,45,45,0.75)' }}>Previous</span>
        </button>

        {/* Page numbers */}
        <div className="hidden md:flex items-center gap-1 px-2">
          {pageNumbers.map((pageNum, idx) => {
            if (pageNum === '...') {
              return (
                <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-400 select-none">...</span>
              );
            }

            const page = pageNum as number;
            const isCurrentPage = page === currentPage;

            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                disabled={isLoading}
                className="group relative min-w-[40px] px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200"
                aria-label={`Go to page ${page}`}
                aria-current={isCurrentPage ? 'page' : undefined}
                style={{
                  background: isCurrentPage ? PRIMARY : 'transparent',
                  color: isCurrentPage ? '#fff' : 'rgba(45,45,45,0.85)',
                  boxShadow: isCurrentPage ? `0 6px 18px ${PRIMARY_DARK}11` : 'none',
                }}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Next page */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={(currentPage === totalPages && !hasNextPage) || isLoading}
          className={`group relative px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5`}
          aria-label="Go to next page"
          style={{
            color: (currentPage === totalPages && !hasNextPage) || isLoading ? 'rgba(45,45,45,0.25)' : 'rgba(45,45,45,0.8)',
          }}
        >
          <span className="hidden sm:inline" style={{ color: 'rgba(45,45,45,0.75)' }}>Next</span>
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last page (only if we know the total) */}
        {!hasNextPage && (
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || isLoading}
            className={`group relative p-2 rounded-lg transition-all duration-200`}
            title="Last page"
            aria-label="Go to last page"
            style={{
              color: currentPage === totalPages || isLoading ? 'rgba(45,45,45,0.25)' : 'rgba(45,45,45,0.7)',
            }}
          >
            <ChevronsRight className="w-5 h-5" />
          </button>
        )}
      </nav>

      {/* Mobile page numbers */}
      <div className="md:hidden flex items-center gap-1 order-3">
        {pageNumbers.slice(0, 5).map((pageNum, idx) => {
          if (pageNum === '...') {
            return (
              <span key={`mobile-ellipsis-${idx}`} className="px-2 py-1 text-gray-400 text-sm">...</span>
            );
          }

          const page = pageNum as number;
          const isCurrentPage = page === currentPage;

          return (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              disabled={isLoading}
              className="min-w-[36px] px-2.5 py-1.5 rounded-lg font-medium text-sm transition-all duration-200"
              aria-label={`Go to page ${page}`}
              aria-current={isCurrentPage ? 'page' : undefined}
              style={{
                background: isCurrentPage ? PRIMARY : 'transparent',
                color: isCurrentPage ? '#fff' : 'rgba(45,45,45,0.85)',
              }}
            >
              {page}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Pagination;
